import { useCallback, useEffect, useState } from 'react'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded'
import { PUBLIC_SITE_CHROME } from '../nls/public/publicSiteChrome'
import type { PublicSiteChrome } from '../nls/public/publicSiteTypes'
import {
  HELPER_APK_DOWNLOAD_URL,
  MEMBER_APK_DOWNLOAD_URL,
  fetchPublishedManifest,
  type PublishedManifest,
  type UpdateSurface,
} from '../services/nativeUpdateService'

type Card = {
  surface: UpdateSurface
  url: string
}

const CARDS: Card[] = [
  {
    surface: 'member',
    url: MEMBER_APK_DOWNLOAD_URL,
  },
  {
    surface: 'helper',
    url: HELPER_APK_DOWNLOAD_URL,
  },
]

const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  if (mb >= 10) return `${Math.round(mb)} MB`
  return `${mb.toFixed(1)} MB`
}

const formatReleaseDate = (iso: string | null): string => {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  try {
    return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return parsed.toISOString().slice(0, 10)
  }
}

const openApk = (url: string) => {
  if (typeof window === 'undefined') return
  const opened = window.open(url, '_blank', 'noopener,noreferrer')
  if (!opened) window.location.assign(url)
}

const copyApk = async (url: string): Promise<boolean> => {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return false
    await navigator.clipboard.writeText(url)
    return true
  } catch {
    return false
  }
}

const shareApk = async (
  surface: UpdateSurface,
  url: string,
  chrome: PublicSiteChrome['download'],
): Promise<'SHARED' | 'COPIED' | 'FAILED'> => {
  if (typeof navigator !== 'undefined' && 'share' in navigator && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title: surface === 'helper' ? chrome.shareTitleHelper : chrome.shareTitleMember,
        text: chrome.shareText,
        url,
      })
      return 'SHARED'
    } catch {
      return 'FAILED'
    }
  }
  return (await copyApk(url)) ? 'COPIED' : 'FAILED'
}

const DownloadCard = ({ card, chrome }: { card: Card; chrome: PublicSiteChrome['download'] }) => {
  const [manifest, setManifest] = useState<PublishedManifest | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const copy = chrome.cards[card.surface]

  useEffect(() => {
    let cancelled = false
    void fetchPublishedManifest(card.surface).then((result) => {
      if (!cancelled) setManifest(result)
    })
    return () => {
      cancelled = true
    }
  }, [card.surface])

  const onCopy = useCallback(async () => {
    const ok = await copyApk(card.url)
    setStatus(ok ? chrome.copiedStatus : chrome.copyUnavailableStatus)
  }, [card.url, chrome.copiedStatus, chrome.copyUnavailableStatus])

  const onShare = useCallback(async () => {
    const result = await shareApk(card.surface, card.url, chrome)
    if (result === 'SHARED') setStatus(chrome.sharedStatus)
    else if (result === 'COPIED') setStatus(chrome.shareCopiedStatus)
    else setStatus(chrome.shareFailedStatus)
  }, [card.surface, card.url, chrome])

  return (
    <article className="publicSignalPanel" data-testid={`download-card-${card.surface}`}>
      <span>{copy.audience}</span>
      <h3 style={{ marginTop: '0.5rem', fontSize: '1.25rem' }}>{copy.title}</h3>
      <p>{copy.body}</p>
      <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', opacity: 0.75, wordBreak: 'break-all' }}>
        <strong>{chrome.directLink}</strong> {card.url}
      </p>
      <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', opacity: 0.75 }}>
        {manifest ? (
          <>
            <strong>{chrome.latestPublished}</strong> {manifest.version}
            {manifest.releasedAt ? ` · ${formatReleaseDate(manifest.releasedAt)}` : ''}
            {formatBytes(manifest.apkSizeBytes) ? ` · ${formatBytes(manifest.apkSizeBytes)}` : ''}
          </>
        ) : (
          <>{chrome.manifestPending}</>
        )}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
        <button
          type="button"
          className="publicMegaButton"
          onClick={() => openApk(card.url)}
          data-testid={`download-button-${card.surface}`}
        >
          <DownloadRoundedIcon />
          {chrome.downloadApk}
          <ArrowOutwardRoundedIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="publicQuietButton"
          onClick={() => void onCopy()}
          data-testid={`copy-button-${card.surface}`}
        >
          <ContentCopyRoundedIcon />
          {chrome.copyLink}
        </button>
        <button
          type="button"
          className="publicQuietButton"
          onClick={() => void onShare()}
          data-testid={`share-button-${card.surface}`}
        >
          <IosShareRoundedIcon />
          {chrome.share}
        </button>
      </div>
      <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', opacity: 0.85 }}>{copy.callout}</p>
      {status ? (
        <p role="status" style={{ marginTop: '0.5rem', fontSize: '0.75rem', opacity: 0.85 }}>
          {status}
        </p>
      ) : null}
    </article>
  )
}

export const DownloadPage = ({ chrome = PUBLIC_SITE_CHROME.download }: { chrome?: PublicSiteChrome['download'] }) => (
  <main className="publicArticle">
    <div className="publicArticleHero">
      <div className="publicArticleIcon"><PhoneAndroidRoundedIcon /></div>
      <p className="publicEyebrow">{chrome.eyebrow}</p>
      <h1>{chrome.title}</h1>
      <p>{chrome.intro}</p>
    </div>

    <section className="publicSignalGrid" aria-label={chrome.cardsAriaLabel}>
      {CARDS.map((card) => <DownloadCard key={card.surface} card={card} chrome={chrome} />)}
    </section>

    <section className="publicSection" aria-labelledby="install-tips-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.tipsEyebrow}</p>
        <h2 id="install-tips-title">{chrome.tipsTitle}</h2>
      </div>
      <div className="publicFeatureGrid">
        {chrome.tips.map((tip) => (
          <article key={tip.title} className="publicFeature">
            <h3>{tip.title}</h3>
            <p>{tip.body}</p>
          </article>
        ))}
      </div>
    </section>
  </main>
)
